// Iter57 — shared barcode scanner hook.
//
// Feature-detects the browser BarcodeDetector API, opens the rear camera,
// polls for a code for up to 12s, and returns the raw value via callback.
// Used by NewSortingRequestModal (iter52) and NewStockLockModal (iter57).
import { useCallback, useMemo, useState } from "react";

const DEFAULT_FORMATS = ["code_128", "ean_13", "ean_8", "qr_code", "code_39", "upc_a", "upc_e"];

export function useBarcodeScanner({ onScanned, onError, timeoutMs = 12000 } = {}) {
  const supported = useMemo(
    () => typeof window !== "undefined" && "BarcodeDetector" in window,
    [],
  );
  const [busy, setBusy] = useState(false);

  const scan = useCallback(async () => {
    if (!supported) {
      onError?.("Barcode scanning is not supported in this browser. Use a Chromium / Android browser, or paste the part number manually.");
      return null;
    }
    setBusy(true);
    let stream;
    let video;
    try {
      // eslint-disable-next-line no-undef
      const detector = new BarcodeDetector({ formats: DEFAULT_FORMATS });
      stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      video = document.createElement("video");
      video.srcObject = stream;
      video.setAttribute("playsinline", "true");
      await video.play();
      const deadline = Date.now() + timeoutMs;
      let found = null;
      while (Date.now() < deadline) {
        try {
          const codes = await detector.detect(video);
          if (codes && codes.length) { found = codes[0].rawValue; break; }
        } catch (_) { /* keep polling */ }
        await new Promise((r) => setTimeout(r, 200));
      }
      if (found) {
        onScanned?.(found);
        return found;
      }
      onError?.("No barcode detected within the time window — try again or type the part number.");
      return null;
    } catch (e) {
      onError?.(`Barcode scan failed: ${e.message || e}`);
      return null;
    } finally {
      if (video) video.pause();
      if (stream) stream.getTracks().forEach((t) => t.stop());
      setBusy(false);
    }
  }, [supported, onScanned, onError, timeoutMs]);

  return { supported, busy, scan };
}
